import React from 'react'
import { SpiSdk } from '@local/spi-js'

type SpiStatusReady = {
  spiSdk: SpiSdk
  status: 'ready'
}

type SpiInitializationReducerState =
  | { status: 'init'; spiSdk: null }
  | SpiStatusReady
  | { status: 'loading'; spiSdk: null }
  | { status: 'error'; spiSdk: null; error: unknown }

type SpiContextInternal = SpiInitializationReducerState
const SpiElementsContext = React.createContext<undefined | SpiContextInternal>(
  undefined
)

export const useSpiInternal = (): SpiContextInternal => {
  const spiContext = React.useContext(SpiElementsContext)
  if (!spiContext) {
    throw new Error(
      'SPI context not available. Are you calling it from outside of a SpiProvider?'
    )
  }
  return spiContext
}

export type SpiContext = { spiSdk: SpiSdk | null }
export const useSpi = (): SpiContext => {
  const spiCtx = useSpiInternal()
  return {
    spiSdk: spiCtx.spiSdk ?? null
  }
}

export const SpiElementsProvider = (props: {
  spiPromise: Promise<SpiSdk>
  children: React.ReactNode
}) => {
  const [spiState, setSpiState] = React.useState<SpiInitializationReducerState>(
    {
      status: 'init',
      spiSdk: null
    }
  )

  const spiPromiseRef = React.useRef(props.spiPromise)
  React.useEffect(() => {
    let isMounted = true
    let isLoading = true
    const spiPromiseRaw = spiPromiseRef.current
    // wait a tick before entering loading state
    // (assume the micro-task queue will complete before the task queue)
    const timeoutId = window.setTimeout(() => {
      if (isLoading && isMounted) {
        isLoading = true
        setSpiState({ spiSdk: null, status: 'loading' })
      }
    }, 0)
    spiPromiseRaw
      .then((sdk) => {
        if (!isMounted) {
          return
        }
        setSpiState({ spiSdk: sdk, status: 'ready' })
      })
      .catch((err: unknown) => {
        if (!isMounted) {
          return
        }
        setSpiState({ spiSdk: null, status: 'error', error: err })
      })
      .finally(() => {
        isLoading = false
      })
    return () => {
      isMounted = false
      window.clearTimeout(timeoutId)
    }
  }, [])

  const spiContextValue: SpiContextInternal = React.useMemo(() => {
    if (spiState.status === 'ready') {
      return {
        spiSdk: spiState.spiSdk,
        status: spiState.status,
        spiPromise: props.spiPromise
      }
    } else if (spiState.status === 'error') {
      return {
        spiSdk: null,
        status: spiState.status,
        error: spiState.error,
        spiPromise: props.spiPromise
      }
    } else {
      return {
        spiSdk: null,
        status: spiState.status,
        spiPromise: props.spiPromise
      }
    }
  }, [spiState, props.spiPromise])

  return (
    <SpiElementsContext.Provider value={spiContextValue}>
      {props.children}
    </SpiElementsContext.Provider>
  )
}
